import { useEffect, useRef, type RefObject } from 'react';

export type UseDismissOptions = {
  open: boolean;
  onDismiss: () => void;
  /** Elements that count as "inside": the floating element and usually its trigger. */
  refs: Array<RefObject<HTMLElement | null>>;
  escape?: boolean;
  outsidePress?: boolean;
};

/**
 * Closes an overlay (Menu, Tooltip, Sheet, Dialog) on Escape and on a press outside every element
 * in `refs`. Listens only while open. Pointerdown rather than click, so a press that starts inside
 * and ends outside (text selection) does not dismiss.
 */
export function useDismiss({ open, onDismiss, refs, escape = true, outsidePress = true }: UseDismissOptions) {
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  const tracked = useRef(refs);
  tracked.current = refs;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (escape && e.key === 'Escape') {
        e.stopPropagation();
        dismiss.current();
      }
    };
    const onPointer = (e: PointerEvent) => {
      if (!outsidePress) return;
      const target = e.target as Node | null;
      if (target && tracked.current.some((r) => r.current?.contains(target))) return;
      dismiss.current();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open, escape, outsidePress]);
}
